import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { DateObject } from "react-multi-date-picker";
import { addAvailabilityArray, removeAvailabilityArray } from "../../api/engineerApi";
import { toast } from "react-toastify";
import { connect } from "react-redux";
import * as profileActions from "../../redux/actions/profileActions";

const DAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const Availability = ({ profile, auth, loadProfile }) => {
  const [formChanged, setFormChanged] = useState(false);
  const [datesAdded, setDatesAdded] = useState([]);
  const [datesRemoved, setDatesRemoved] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(async () => {
    if (profile && Object.keys(profile).length === 0) {
      await loadProfile(auth).catch((error) => {
        console.log("error loading profile");
        throw error;
      });
    }
  }, [profile]);

  useEffect(() => {
    if (formChanged) {
      window.addEventListener("beforeunload", alertUser);
      return () => window.removeEventListener("beforeunload", alertUser);
    } else {
      window.removeEventListener("beforeunload", alertUser);
    }
  }, [formChanged]);

  const alertUser = (e) => {
    e.preventDefault();
    e.returnValue = "";
  };

  const previousDates = () => {
    return (profile.availability || [])
      .map((a) => new DateObject(a.dateAvailable))
      .filter((d) => d.toDate() >= today);
  };

  useEffect(() => {
    setAvailableDates(previousDates());
  }, [profile]);

  const isDateAvailable = (date) =>
    availableDates.some((d) => d.toDate().toDateString() === date.toDateString());

  const checkDateExistsInProfile = (date) =>
    (profile.availability || []).some(
      (profileDate) => new Date(profileDate.dateAvailable).toDateString() === date.toDateString()
    );

  const handleDayClick = (date) => {
    if (date < today) return;

    const dateStr = date.toDateString();
    const isSelected = isDateAvailable(date);
    const existsInProfile = checkDateExistsInProfile(date);
    const dateObj = new DateObject(date);

    setFormChanged(true);

    if (isSelected) {
      setAvailableDates((prev) => prev.filter((d) => d.toDate().toDateString() !== dateStr));
      if (existsInProfile) {
        setDatesRemoved((prev) => [...prev, dateObj]);
      } else {
        setDatesAdded((prev) => prev.filter((d) => d.toDate().toDateString() !== dateStr));
      }
    } else {
      setAvailableDates((prev) => [...prev, dateObj]);
      if (!existsInProfile) {
        setDatesAdded((prev) => [...prev, dateObj]);
      } else {
        setDatesRemoved((prev) => prev.filter((d) => d.toDate().toDateString() !== dateStr));
      }
    }
  };

  const buildMonthDays = (baseDate) => {
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Mon-based offset (Mon=0 ... Sun=6)
    let startOffset = firstDay.getDay() - 1;
    if (startOffset === -1) startOffset = 6;

    const days = [];
    for (let i = 0; i < startOffset; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));

    return days;
  };

  const prevMonth = () =>
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));

  const nextMonth = () =>
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

  const isPrevDisabled =
    currentMonth.getFullYear() === today.getFullYear() &&
    currentMonth.getMonth() === today.getMonth();

  const monthLabel = currentMonth.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const monthDays = buildMonthDays(currentMonth);

  const saveChanges = async () => {
    setFormChanged(false);

    toast.success("Saving", {
      autoClose: 2000,
      hideProgressBar: true,
      position: "top-right",
      closeButton: false,
      icon: false,
    });

    try {
      const datesToAdd = datesAdded.map((d) => d.toDate().toDateString());
      const datesToRemove = datesRemoved.map((d) => d.toDate().toDateString());

      if (datesToAdd.length > 0) await addAvailabilityArray(auth, datesToAdd);
      if (datesToRemove.length > 0) await removeAvailabilityArray(auth, datesToRemove);

      setDatesAdded([]);
      setDatesRemoved([]);
      await loadProfile(auth);
    } catch (error) {
      toast.error("Error saving", { autoClose: 2000, hideProgressBar: true, position: "top-right", closeButton: false, icon: false });
    }
  };

  const cancelChanges = () => {
    setFormChanged(false);
    setDatesAdded([]);
    setDatesRemoved([]);
    setAvailableDates(previousDates());
  };

  return (
    <>
      {/* Month navigation */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm px-3"
          onClick={prevMonth}
          disabled={isPrevDisabled}
        >
          ‹
        </button>
        <span className="fw-semibold">{monthLabel}</span>
        <button type="button" className="btn btn-outline-secondary btn-sm px-3" onClick={nextMonth}>
          ›
        </button>
      </div>

      {/* Day-of-week headers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", marginBottom: "4px" }}>
        {DAY_HEADERS.map((h) => (
          <div
            key={h}
            className="text-center fw-semibold text-muted"
            style={{ fontSize: "0.75rem", padding: "4px 0" }}
          >
            {h}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px" }}>
        {monthDays.map((date, i) => {
          if (!date) return <div key={`empty-${i}`} />;

          const isPast = date < today;
          const isToday = date.toDateString() === today.toDateString();
          const isAvailable = isDateAvailable(date);
          const dayOfWeek = date.getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

          let bg = "transparent";
          let color = isWeekend ? "#dc3545" : "#212529";
          let border = "1px solid #dee2e6";
          let opacity = isPast ? 0.3 : 1;

          if (isAvailable) {
            bg = "#198754";
            color = "#fff";
            border = "1px solid #198754";
          } else if (isToday) {
            border = "2px solid #0d6efd";
          }

          return (
            <button
              key={date.toDateString()}
              type="button"
              onClick={() => handleDayClick(date)}
              disabled={isPast}
              style={{
                background: bg,
                color,
                border,
                borderRadius: "8px",
                padding: "10px 4px",
                fontSize: "1rem",
                fontWeight: isToday ? "700" : "500",
                cursor: isPast ? "default" : "pointer",
                opacity,
                touchAction: "manipulation",
                lineHeight: 1,
                minHeight: "48px",
              }}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      <div className="d-flex gap-3 mt-3 align-items-center">
        <div className="d-flex align-items-center gap-1">
          <div style={{ width: 16, height: 16, background: "#198754", borderRadius: 3 }} />
          <small className="text-muted">Available</small>
        </div>
        <div className="d-flex align-items-center gap-1">
          <div style={{ width: 16, height: 16, border: "2px solid #0d6efd", borderRadius: 3 }} />
          <small className="text-muted">Today</small>
        </div>
        <div className="d-flex align-items-center gap-1">
          <small style={{ color: "#dc3545", fontWeight: 600 }}>S</small>
          <small className="text-muted">Weekend</small>
        </div>
      </div>

      {formChanged && (
        <div className="row mt-3">
          <div className="col-12 col-md-6 offset-md-3">
            <div className="d-grid gap-2">
              <button type="submit" className="btn btn-success" onClick={saveChanges}>
                Save Changes
              </button>
              <button type="button" className="btn btn-outline-danger" onClick={cancelChanges}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

Availability.propTypes = {
  profile: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  loadProfile: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    profile: state.profile,
  };
}

const mapDispatchToProps = {
  loadProfile: profileActions.loadProfile,
};

export default connect(mapStateToProps, mapDispatchToProps)(Availability);
